import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, Brain, Target, Award, Shield, 
  Briefcase, MessageSquare, Building2, Users,
  CheckCircle, Rocket, BarChart, Book, Globe,
  Star, Code, Database, FileText, Zap, Cloud
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/home';
import FloatingChatbot from '../components/FloatingChatbot';

const HomePage = () => {
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <div className="bg-background">
      {/* Floating Chatbot */}
      <FloatingChatbot />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 to-indigo-900/20"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground tracking-tight">
              {t.hero.title}
            </h1>
            <p className="mt-6 text-xl md:text-2xl text-foreground/80">
              {t.hero.subtitle}
            </p>
            <p className="mt-4 text-lg text-foreground/70">
              {t.hero.description}
            </p>
            <p className="mt-4 text-xl text-foreground font-semibold">
              {t.hero.claim}
            </p>
            <div className="mt-10 flex justify-center space-x-4">
              <Link
                to="/registro"
                className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-accent hover:bg-accent/90 transition-all duration-300"
              >
                {t.cta.buttons.learner}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/employer/login"
                className="inline-flex items-center px-8 py-3 border border-accent text-base font-medium rounded-lg text-accent hover:bg-accent/10 transition-all duration-300"
              >
                {t.cta.buttons.employer}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Learners Section */}
      <div className="py-24 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-foreground">
              {t.learners.title}
            </h2>
            <p className="mt-4 text-xl text-foreground/80">
              {t.learners.intro}
            </p>
          </div>

          {/* Roles */}
          <div className="mb-16">
            <h3 className="text-xl font-bold text-foreground mb-8 text-center">
              {t.learners.roles.title}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {t.learners.roles.list.map((role, index) => (
                <div key={index} className="dark-card p-6 hover:border-accent/50 transition-all duration-300">
                  <div className="flex items-center mb-4">
                    <Cloud className="h-8 w-8 text-accent" />
                    <h4 className="ml-3 text-lg font-medium text-foreground">{role}</h4>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Skills */}
          <div className="mb-16">
            <h3 className="text-xl font-bold text-foreground mb-8 text-center">
              {t.learners.skills.title}
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {t.learners.skills.list.map((skill, index) => (
                <div key={index} className="flex items-center p-4 bg-accent/10 rounded-lg">
                  <Code className="h-5 w-5 text-accent mr-3" />
                  <span className="text-foreground">{skill}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Process */}
          <div>
            <h3 className="text-xl font-bold text-foreground mb-8 text-center">
              {t.learners.process.title}
            </h3>
            <div className="space-y-8">
              {t.learners.process.steps.map((step, index) => (
                <div key={index} className="dark-card p-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="h-8 w-8 rounded-full bg-accent text-white flex items-center justify-center">
                        {index + 1}
                      </div>
                    </div>
                    <p className="ml-4 text-foreground">{step}</p>
                  </div>
                  {index === 2 && (
                    <div className="mt-4 ml-12">
                      <ul className="space-y-3">
                        {t.learners.process.dcvee.map((item, i) => (
                          <li key={i} className="flex items-start">
                            <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                            <span className="ml-3 text-foreground">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
              <div className="text-center">
                <p className="text-xl font-bold text-foreground">
                  {t.learners.process.outcome}
                </p>
                <p className="mt-4 text-foreground/70 italic">
                  {t.learners.process.payment}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Previews */}
      <div className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Student Dashboard */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-foreground mb-8 text-center">
              {t.dashboard.student.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {t.dashboard.student.features.map((feature, index) => (
                <div key={index} className="dark-card p-6 hover:border-accent/50 transition-all duration-300">
                  <div className="flex items-start">
                    <BarChart className="h-6 w-6 text-accent" />
                    <p className="ml-3 text-foreground">{feature}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Employer Dashboard */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-8 text-center">
              {t.dashboard.employer.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {t.dashboard.employer.features.map((feature, index) => (
                <div key={index} className="dark-card p-6 hover:border-accent/50 transition-all duration-300">
                  <div className="flex items-start">
                    <Target className="h-6 w-6 text-accent" />
                    <p className="ml-3 text-foreground">{feature}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Use Cases */}
      <div className="py-24 bg-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Employer Cases */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-foreground mb-8">
              {t.cases.employers.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {t.cases.employers.cases.map((useCase, index) => (
                <div key={index} className="dark-card p-6 hover:border-accent/50 transition-all duration-300">
                  <Building2 className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">{useCase.title}</h3>
                  <p className="text-foreground/70">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Learner Cases */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-8">
              {t.cases.learners.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {t.cases.learners.cases.map((useCase, index) => (
                <div key={index} className="dark-card p-6 hover:border-accent/50 transition-all duration-300">
                  <Users className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-lg font-medium text-foreground mb-2">{useCase.title}</h3>
                  <p className="text-foreground/70">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Learner Testimonials */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-foreground mb-8 text-center">
              {t.testimonials.learners.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {t.testimonials.learners.list.map((testimonial, index) => (
                <div key={index} className="dark-card p-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <Star className="h-8 w-8 text-yellow-500" />
                    </div>
                    <div className="ml-4">
                      <p className="text-foreground italic">"{testimonial.quote}"</p>
                      <div className="mt-4">
                        <p className="text-foreground font-medium">{testimonial.author}</p>
                        <p className="text-foreground/70">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Employer Testimonials */}
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-8 text-center">
              {t.testimonials.employers.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {t.testimonials.employers.list.map((testimonial, index) => (
                <div key={index} className="dark-card p-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <Building2 className="h-8 w-8 text-accent" />
                    </div>
                    <div className="ml-4">
                      <p className="text-foreground italic">"{testimonial.quote}"</p>
                      <div className="mt-4">
                        <p className="text-foreground font-medium">{testimonial.author}</p>
                        <p className="text-foreground/70">{testimonial.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-purple-900 to-indigo-900">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:py-16 lg:px-8 lg:flex lg:items-center lg:justify-between">
          <h2 className="text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            <span className="block">{t.cta.title}</span>
          </h2>
          <div className="mt-8 flex lg:mt-0 lg:flex-shrink-0 space-x-4">
            <Link
              to="/registro"
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-purple-900 bg-white hover:bg-gray-50"
            >
              {t.cta.buttons.learner}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/employer/login"
              className="inline-flex items-center px-6 py-3 border border-white text-base font-medium rounded-md text-white hover:bg-white/10"
            >
              {t.cta.buttons.employer}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomePage;